import React from 'react';
import { Check, Pencil } from 'lucide-react';
import { Label } from '@/app/components/ui/Label';
import { toTitleCase } from '@/lib/utils';

interface QuickAddPerson {
  fullName: string;
  relationship?: string;
  address?: any;
  [key: string]: any;
}

interface QuickAddPeopleProps {
  people: QuickAddPerson[];
  selectedPeople: QuickAddPerson[]; // Or just list of names/identifiers
  onToggle: (person: QuickAddPerson) => void;
  onEdit?: (person: QuickAddPerson, e: React.MouseEvent) => void;
  heading?: string;
  isSelected?: (personName: string) => boolean; // Takes person name string
  emptyMessage?: string;
}

export const QuickAddPeople = ({
  people,
  selectedPeople,
  onToggle,
  onEdit,
  heading = "Quick add from saved people:",
  isSelected,
  emptyMessage
}: QuickAddPeopleProps) => {
  if (people.length === 0) {
    if (emptyMessage) {
      return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
    }
    return null;
  }

  // Check selection - use isSelected function if provided, otherwise check selectedPeople array
  const checkSelected = (person: QuickAddPerson): boolean => {
    if (isSelected) {
      return isSelected(person.fullName);
    }
    return selectedPeople.some(p => p.fullName === person.fullName);
  };

  return (
    <div className="space-y-3">
      {heading && <Label className="text-base font-medium text-gray-700">{heading}</Label>}
      <div className="grid grid-cols-1 gap-3 staggered-fade-in">
        {people.map((person, i) => {
          const selected = checkSelected(person);
          return (
            <button
              key={`${person.fullName}-${i}`}
              type="button"
              onClick={() => onToggle(person)}
              className={`group flex items-center gap-4 p-4 rounded-xl transition-all duration-300 text-left relative overflow-hidden ${selected
                ? 'bg-white/90 backdrop-blur-sm border-2 border-primary shadow-[0_4px_20px_rgba(20,184,166,0.15)]'
                : 'bg-white border border-gray-200 hover:border-primary/30 hover:shadow-md hover:scale-[1.01]'
                }`}
            >
              <div
                className={`h-6 w-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-all duration-300 ${selected
                  ? 'border-primary bg-primary text-white shadow-sm scale-110'
                  : 'border-gray-200 bg-gray-50 group-hover:border-primary/30'
                  }`}
              >
                {selected && <Check className="h-3.5 w-3.5" />}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className={`text-base font-bold truncate transition-colors ${selected ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'}`}>
                  {toTitleCase(person.fullName)}
                </span>
                <span className="text-sm text-muted-foreground font-medium">
                  {toTitleCase(person.relationship) || 'Person'}
                </span>
              </div>
              {onEdit && (
                <div
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(person, e);
                  }}
                  className={`p-2 rounded-full transition-all duration-200 ${selected
                    ? 'text-primary/80 hover:text-primary hover:bg-primary/10'
                    : 'text-gray-300 hover:text-primary hover:bg-primary/5'
                    }`}
                >
                  <Pencil className="h-4 w-4" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

